<template>
  <v-dialog
    :value="visible && isKorean"
    max-width="460"
    content-class="lafia-inapp-dialog"
    overlay-color="#25191c"
    :overlay-opacity="0.68"
    persistent
    @input="handleVisibility"
  >
    <section
      class="royal-card"
      role="dialog"
      aria-modal="true"
      aria-labelledby="inapp-dialog-title"
      :lang="languageCode"
    >
      <div class="royal-card__inner">
        <div class="royal-emblem" aria-hidden="true">
          <img src="@/assets/deco/inapp-royal-emblem-transparent.png" alt="" class="royal-emblem__castle" />
        </div>
        <div class="royal-wordmark" aria-hidden="true">
          <span></span><b>THE LAFIA KINGDOM</b><span></span>
        </div>

        <template v-if="screen === 'choice'">
          <h2 id="inapp-dialog-title" class="royal-title">{{ $t("dialog.inweb-required-title") }}</h2>
          <p class="royal-note royal-note--compact">
            <span class="browser-thumbnails" aria-hidden="true">
              <span class="browser-thumbnail browser-thumbnail--kakao"></span>
              <span class="browser-thumbnail browser-thumbnail--naver">N</span>
            </span>
            <span>{{ $t("dialog.inweb-other-browser-warning") }}</span>
          </p>

          <div class="option-list" :aria-label="$t('dialog.inweb-open-method-label')">
            <button type="button" class="option-row option-row--primary" @click="openChrome">
              <span class="chrome-icon" aria-hidden="true"></span>
              <span class="option-row__text">
                <strong>{{ $t("dialog.inweb-chrome-open") }}</strong>
                <small>{{ $t("dialog.inweb-chrome-open-note") }}</small>
              </span>
              <span class="chevron" aria-hidden="true">›</span>
            </button>
            <button type="button" class="option-row" @click="openSafariGuide('choice')">
              <span class="safari-icon option-row__safari" aria-hidden="true"></span>
              <span class="option-row__text">
                <strong>{{ $t("dialog.inweb-safari-guide-open") }}</strong>
                <small>{{ $t("dialog.inweb-safari-guide-note") }}</small>
              </span>
              <span class="chevron" aria-hidden="true">›</span>
            </button>
          </div>
        </template>

        <template v-else-if="screen === 'chrome-failed'">
          <h2 id="inapp-dialog-title" class="royal-title failure-title">
            {{ $t("dialog.inweb-chrome-failed-title") }}
          </h2>
          <p class="failure-note">{{ $t("dialog.inweb-chrome-failed-note") }}</p>

          <div class="failure-actions">
            <button type="button" class="royal-primary" @click="installChrome">
              <span class="chrome-icon" aria-hidden="true"></span>
              {{ $t("dialog.inweb-chrome-install") }}
            </button>
            <button type="button" class="failure-secondary" @click="openSafariGuide('chrome-failed')">
              <span class="safari-icon" aria-hidden="true"></span>
              {{ $t("dialog.inweb-safari-guide-open") }}
            </button>
          </div>
        </template>

        <template v-else>
          <h2 id="inapp-dialog-title" class="royal-title safari-guide-title">
            {{ $t("dialog.inweb-safari-guide-title") }}
          </h2>
          <p class="safari-guide-lead">{{ $t("dialog.inweb-safari-guide-lead") }}</p>

          <div class="safari-steps" :aria-label="$t('dialog.inweb-safari-steps-label')">
            <div class="safari-step">
              <span class="safari-step__number">1</span>
              <span class="menu-dots-icon" aria-hidden="true"><i></i><i></i><i></i></span>
              <span class="safari-step__text">
                <strong>{{ $t("dialog.inweb-safari-step-menu") }}</strong>
                <small>{{ $t("dialog.inweb-safari-step-menu-note") }}</small>
              </span>
            </div>
            <div class="safari-step">
              <span class="safari-step__number">2</span>
              <span class="safari-icon safari-step__icon" aria-hidden="true"></span>
              <span class="safari-step__text">
                <strong>{{ $t("dialog.inweb-safari-step-open") }}</strong>
                <small>{{ $t("dialog.inweb-safari-step-open-note") }}</small>
              </span>
            </div>
          </div>
          <button type="button" class="guide-back" @click="returnFromSafariGuide">
            {{ $t("dialog.inweb-guide-back") }}
          </button>
        </template>
      </div>
    </section>
  </v-dialog>
</template>

<script>
const CHROME_OPEN_TIMEOUT_MS = 1400
const CHROME_APP_STORE_URL = "https://apps.apple.com/kr/app/google-chrome/id535886823"

export default {
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    screen: "choice",
    safariGuideReturnScreen: "choice",
    chromeOpenTimer: null,
    chromeOpenStartedAt: 0,
  }),

  computed: {
    isKorean() {
      return this.$i18n.locale === "ko"
    },

    languageCode() {
      return this.$i18n.locale === "jp" ? "ja" : this.$i18n.locale
    },
  },

  watch: {
    visible(value) {
      if (value) {
        this.resetDialog()
      } else {
        this.cleanupChromeAttempt()
      }
    },
  },

  beforeDestroy() {
    this.cleanupChromeAttempt()
  },

  methods: {
    handleVisibility(value) {
      this.$emit("input", value)
    },

    resetDialog() {
      this.cleanupChromeAttempt()
      this.screen = "choice"
      this.safariGuideReturnScreen = "choice"
    },

    buildChromeUrl() {
      const currentUrl = window.location.href
      if (currentUrl.indexOf("https://") === 0) return currentUrl.replace(/^https:/, "googlechromes:")
      if (currentUrl.indexOf("http://") === 0) return currentUrl.replace(/^http:/, "googlechrome:")
      return null
    },

    openChrome() {
      const chromeUrl = this.buildChromeUrl()
      if (!chromeUrl) {
        this.screen = "chrome-failed"
        return
      }

      this.cleanupChromeAttempt()
      this.chromeOpenStartedAt = Date.now()
      document.addEventListener("visibilitychange", this.handleChromeHandoff)
      window.addEventListener("pagehide", this.handleChromeHandoff)
      this.chromeOpenTimer = window.setTimeout(this.handleChromeOpenTimeout, CHROME_OPEN_TIMEOUT_MS)
      window.location.href = chromeUrl
    },

    handleChromeHandoff(event) {
      if (document.hidden || event.type === "pagehide") this.cleanupChromeAttempt()
    },

    handleChromeOpenTimeout() {
      const elapsed = Date.now() - this.chromeOpenStartedAt
      this.cleanupChromeAttempt()
      if (!document.hidden && elapsed < CHROME_OPEN_TIMEOUT_MS * 3) this.screen = "chrome-failed"
    },

    cleanupChromeAttempt() {
      if (this.chromeOpenTimer) window.clearTimeout(this.chromeOpenTimer)
      this.chromeOpenTimer = null
      this.chromeOpenStartedAt = 0
      document.removeEventListener("visibilitychange", this.handleChromeHandoff)
      window.removeEventListener("pagehide", this.handleChromeHandoff)
    },

    installChrome() {
      window.location.href = CHROME_APP_STORE_URL
    },

    openSafariGuide(returnScreen) {
      this.cleanupChromeAttempt()
      this.safariGuideReturnScreen = returnScreen
      this.screen = "safari-guide"
    },

    returnFromSafariGuide() {
      this.screen = this.safariGuideReturnScreen
    },
  },
}
</script>

<style scoped>
::v-deep .lafia-inapp-dialog {
  width: calc(100vw - 24px) !important;
  min-width: 0;
  margin: 12px;
  overflow: visible;
  box-shadow: none;
}

.royal-card,
.royal-card * {
  box-sizing: border-box;
}

.royal-card {
  position: relative;
  width: 100%;
  min-width: 0;
  max-height: calc(100vh - 92px);
  margin-top: 68px;
  overflow: visible;
  padding: 9px;
  color: #493a35;
  background: #fffaf1;
  border: 1px solid #d4ad68;
  border-radius: 28px;
  box-shadow:
    0 24px 58px rgba(31, 19, 20, 0.34),
    0 4px 12px rgba(31, 19, 20, 0.18);
  isolation: isolate;
}

.royal-card::before,
.royal-card__inner::before {
  position: absolute;
  left: 13%;
  width: 74%;
  background: inherit;
  border: inherit;
  border-bottom: 0;
  border-radius: 50% 50% 0 0 / 100% 100% 0 0;
  content: "";
}

.royal-card::before {
  top: -68px;
  z-index: 0;
  height: 78px;
}

.royal-card::after,
.royal-card__inner::after {
  position: absolute;
  left: 13%;
  width: 74%;
  height: 12px;
  background: inherit;
  content: "";
}

.royal-card::after {
  top: -1px;
  z-index: 1;
}

.royal-card__inner {
  position: relative;
  z-index: 2;
  min-width: 0;
  min-height: 540px;
  padding: 8px 36px 28px;
  text-align: center;
  background: #fffaf3;
  border: 1px solid rgba(194, 144, 66, 0.82);
  border-radius: 21px;
}

.royal-card__inner::before {
  top: -59px;
  z-index: 0;
  height: 68px;
}

.royal-card__inner::after {
  top: -1px;
  z-index: 1;
}

.royal-card__inner > * {
  position: relative;
  z-index: 2;
}

.royal-emblem {
  display: flex;
  height: 158px;
  align-items: center;
  justify-content: center;
  margin-top: -48px;
}

.royal-emblem__castle {
  display: block;
  width: 230px;
  max-width: 78%;
  height: auto;
  mix-blend-mode: multiply;
}

.royal-wordmark {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 13px;
  margin: 2px auto 20px;
  color: #a77a39;
}

.royal-wordmark span {
  width: 38px;
  height: 1px;
  background: #d2aa68;
}

.royal-wordmark b {
  font-family: Georgia, "Times New Roman", serif;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.18em;
  white-space: nowrap;
}

.royal-title {
  margin: 0;
  color: #a34054;
  font-family: "Cafe24Danjunghae", "NanumSquare", serif;
  font-size: clamp(25px, 6.9vw, 34px);
  font-weight: 400;
  line-height: 1.28;
  white-space: pre-line;
  word-break: keep-all;
}

.royal-note--compact {
  display: flex;
  min-height: 28px;
  max-width: 100%;
  align-items: center;
  justify-content: center;
  gap: 7px;
  margin: 9px 0 16px;
  color: #645652;
  font-family: "NanumSquare", sans-serif;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.35;
  white-space: nowrap;
}

.browser-thumbnails {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
}

.browser-thumbnail {
  position: relative;
  display: grid;
  width: 22px;
  height: 22px;
  place-items: center;
  border: 1.5px solid #fff;
  border-radius: 7px;
  box-shadow: 0 2px 5px rgba(63, 45, 45, 0.15);
}

.browser-thumbnail + .browser-thumbnail {
  margin-left: -3px;
}

.browser-thumbnail--kakao {
  background: #fee500;
}

.browser-thumbnail--kakao::before {
  width: 12px;
  height: 9px;
  background: #352720;
  border-radius: 50%;
  content: "";
}

.browser-thumbnail--naver {
  color: #fff;
  background: #03c75a;
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: 900;
}

.chrome-icon,
.safari-icon {
  position: relative;
  display: inline-block;
  width: var(--icon-size, 31px);
  height: var(--icon-size, 31px);
  flex: 0 0 var(--icon-size, 31px);
  border-radius: 50%;
}

.chrome-icon {
  background: conic-gradient(#db4437 0 33%, #f4b400 33% 66%, #0f9d58 66% 100%);
  border: 2px solid rgba(255, 255, 255, 0.95);
}

.chrome-icon::after {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 40%;
  height: 40%;
  background: #4285f4;
  border: 2px solid #fff;
  border-radius: 50%;
  content: "";
  transform: translate(-50%, -50%);
}

.safari-icon {
  overflow: hidden;
  background: linear-gradient(145deg, #7fdbff, #0877dc);
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px rgba(8, 119, 220, 0.16);
}

.safari-icon::before {
  position: absolute;
  inset: 17%;
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  content: "";
}

.safari-icon::after {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 11%;
  height: 74%;
  background: linear-gradient(#e34252 0 50%, #fff 50%);
  border-radius: 999px;
  content: "";
  transform: translate(-50%, -50%) rotate(40deg);
}

.option-list,
.failure-actions,
.safari-steps {
  display: grid;
  gap: 9px;
}

.option-row {
  display: grid;
  grid-template-columns: 43px minmax(0, 1fr) 18px;
  width: 100%;
  min-height: 64px;
  align-items: center;
  padding: 8px 14px;
  color: #584641;
  background: #fffdf8;
  border: 1px solid rgba(201, 148, 91, 0.6);
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
  transition: transform 160ms ease, filter 160ms ease;
}

.option-row--primary {
  color: #fff;
  background: #b85569;
  border: 2px solid #c9945b;
  box-shadow:
    inset 0 0 0 2px rgba(255, 236, 196, 0.34),
    0 6px 13px rgba(117, 42, 59, 0.16);
}

.option-row__text,
.option-row strong,
.option-row small {
  display: block;
  min-width: 0;
}

.option-row strong {
  margin-bottom: 2px;
  font-family: "Cafe24Danjunghae", "NanumSquare", serif;
  font-size: 18px;
  font-weight: 400;
}

.option-row small {
  color: #8b7771;
  font-family: "NanumSquare", sans-serif;
  font-size: 10px;
  font-weight: 700;
}

.option-row--primary small {
  color: rgba(255, 255, 255, 0.78);
}

.option-row__safari {
  --icon-size: 29px;
}

.chevron {
  font-family: Georgia, serif;
  font-size: 24px;
  text-align: right;
}

.royal-primary,
.failure-secondary {
  display: flex;
  width: 100%;
  min-height: 62px;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 18px;
  border: 2px solid #c9945b;
  border-radius: 13px;
  cursor: pointer;
  font-family: "Cafe24Danjunghae", "NanumSquare", serif;
  font-size: 22px;
  line-height: 1.25;
  transition: transform 160ms ease, filter 160ms ease;
}

.royal-primary {
  color: #fffdf8;
  background: #b85569;
  box-shadow:
    inset 0 0 0 2px rgba(255, 236, 196, 0.42),
    0 8px 16px rgba(117, 42, 59, 0.2);
}

.failure-secondary {
  color: #594b46;
  background: #fffdf8;
  box-shadow: inset 0 0 0 2px rgba(255, 236, 196, 0.32);
}

.option-row:hover,
.royal-primary:hover,
.failure-secondary:hover {
  filter: brightness(1.04);
  transform: translateY(-1px);
}

.option-row:active,
.royal-primary:active,
.failure-secondary:active {
  transform: scale(0.985);
}

.option-row:focus-visible,
.royal-primary:focus-visible,
.failure-secondary:focus-visible,
.guide-back:focus-visible {
  outline: 3px solid rgba(184, 85, 105, 0.35);
  outline-offset: 3px;
}

.failure-title {
  font-size: clamp(29px, 7.6vw, 35px);
  line-height: 1.22;
}

.failure-note {
  margin: 14px 0 22px;
  color: #645652;
  font-family: "NanumSquare", sans-serif;
  font-size: 16px;
  line-height: 1.55;
  white-space: pre-line;
  word-break: keep-all;
}

.failure-actions {
  gap: 10px;
}

.safari-guide-title {
  font-size: clamp(29px, 7.4vw, 34px);
  line-height: 1.24;
}

.safari-guide-lead {
  margin: 10px 0 17px;
  color: #76635e;
  font-family: "NanumSquare", sans-serif;
  font-size: 13px;
  line-height: 1.45;
  word-break: keep-all;
}

.safari-step {
  display: grid;
  grid-template-columns: 31px 48px minmax(0, 1fr);
  min-height: 72px;
  align-items: center;
  gap: 9px;
  padding: 9px 13px;
  background: #fffdf8;
  border: 1px solid rgba(201, 148, 91, 0.68);
  border-radius: 13px;
  text-align: left;
}

.safari-step__number {
  display: grid;
  width: 27px;
  height: 27px;
  place-items: center;
  color: #fff;
  background: #b85569;
  border-radius: 50%;
  font-family: Georgia, serif;
  font-size: 15px;
  font-weight: 700;
}

.menu-dots-icon {
  display: flex;
  width: 42px;
  height: 42px;
  align-items: center;
  justify-content: center;
  gap: 3px;
  color: #725c56;
  background: #f7eee7;
  border: 1px solid #dcc4ae;
  border-radius: 12px;
}

.menu-dots-icon i {
  width: 4px;
  height: 4px;
  background: currentColor;
  border-radius: 50%;
}

.safari-step__icon {
  --icon-size: 42px;
}

.safari-step__text,
.safari-step__text strong,
.safari-step__text small {
  display: block;
  min-width: 0;
}

.safari-step__text strong {
  margin-bottom: 3px;
  color: #493a35;
  font-family: "Cafe24Danjunghae", "NanumSquare", serif;
  font-size: 17px;
  font-weight: 400;
  line-height: 1.25;
  word-break: keep-all;
}

.safari-step__text small {
  color: #8a7670;
  font-family: "NanumSquare", sans-serif;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.35;
  word-break: keep-all;
}

.guide-back {
  width: 100%;
  min-height: 46px;
  margin-top: 10px;
  color: #594b46;
  background: transparent;
  border: 0;
  border-bottom: 1px solid rgba(198, 153, 78, 0.58);
  cursor: pointer;
  font-family: "Cafe24Danjunghae", "NanumSquare", serif;
  font-size: 17px;
}

[lang="ja"] .royal-title,
[lang="ja"] .royal-note--compact,
[lang="ja"] .option-row strong,
[lang="ja"] .option-row small,
[lang="ja"] .royal-primary,
[lang="ja"] .failure-secondary,
[lang="ja"] .failure-note,
[lang="ja"] .safari-guide-lead,
[lang="ja"] .safari-step__text strong,
[lang="ja"] .safari-step__text small,
[lang="ja"] .guide-back {
  font-family: "Noto Sans JP", sans-serif;
}

@media (max-width: 420px) {
  .royal-card {
    padding: 7px;
    border-radius: 24px;
  }

  .royal-card__inner {
    min-height: 500px;
    padding: 6px 24px 22px;
    border-radius: 18px;
  }

  .royal-emblem {
    height: 145px;
    margin-top: -44px;
  }

  .royal-emblem__castle {
    width: 210px;
  }

  .royal-wordmark {
    gap: 9px;
    margin-bottom: 17px;
  }

  .royal-wordmark span {
    width: 22px;
  }

  .royal-wordmark b {
    font-size: 11px;
    letter-spacing: 0.14em;
  }

  .royal-title {
    font-size: 26px;
  }
}

@media (max-height: 740px) {
  .royal-card {
    margin-top: 56px;
  }

  .royal-card::before {
    top: -56px;
    height: 66px;
  }

  .royal-card__inner {
    min-height: auto;
    padding-top: 4px;
  }

  .royal-card__inner::before {
    top: -47px;
    height: 56px;
  }

  .royal-emblem {
    height: 112px;
    margin-top: -36px;
  }

  .royal-emblem__castle {
    width: 172px;
  }

  .royal-wordmark {
    margin-bottom: 11px;
  }

  .royal-title {
    font-size: 23px;
  }

  .royal-note--compact {
    margin: 7px 0 10px;
  }

  .option-row {
    min-height: 56px;
  }
}

@media (max-width: 400px) {
  ::v-deep .lafia-inapp-dialog {
    width: calc(100vw - 12px) !important;
    margin-right: 6px;
    margin-left: 6px;
  }

  .royal-card {
    margin-top: 60px;
    padding: 5px;
    border-radius: 21px;
  }

  .royal-card::before {
    top: -60px;
    height: 70px;
  }

  .royal-card__inner {
    min-height: 470px;
    padding: 4px 12px 18px;
    border-radius: 16px;
  }

  .royal-card__inner::before {
    top: -51px;
    height: 60px;
  }

  .royal-emblem {
    height: 124px;
    margin-top: -39px;
  }

  .royal-emblem__castle {
    width: 180px;
  }

  .royal-wordmark {
    gap: 5px;
    margin-bottom: 14px;
  }

  .royal-wordmark span {
    width: 12px;
  }

  .royal-wordmark b {
    font-size: 9px;
    letter-spacing: 0.1em;
  }

  .royal-title {
    font-size: clamp(18px, 6.8vw, 24px);
    line-height: 1.25;
  }

  .royal-note--compact {
    gap: 5px;
    margin: 8px auto 13px;
    font-size: clamp(8px, 2.6vw, 9px);
    line-height: 1.3;
    white-space: normal;
  }

  .royal-note--compact > span:last-child {
    max-width: 210px;
    text-align: left;
  }

  .browser-thumbnail {
    width: 19px;
    height: 19px;
    border-radius: 6px;
  }

  .browser-thumbnail--kakao::before {
    width: 10px;
    height: 7px;
  }

  .browser-thumbnail--naver {
    font-size: 10px;
  }

  .option-row {
    grid-template-columns: 34px minmax(0, 1fr) 13px;
    min-height: 56px;
    padding: 7px 9px;
  }

  .option-row .chrome-icon {
    --icon-size: 26px;
  }

  .option-row__safari {
    --icon-size: 26px;
  }

  .option-row strong {
    font-size: 15px;
  }

  .option-row small {
    font-size: 8px;
  }

  .failure-note {
    margin: 10px 0 16px;
    font-size: 14px;
  }

  .failure-title {
    font-size: clamp(25px, 7.8vw, 29px);
  }

  .royal-primary,
  .failure-secondary {
    min-height: 54px;
    padding: 10px 12px;
    font-size: 18px;
  }

  .failure-actions .chrome-icon,
  .failure-actions .safari-icon {
    --icon-size: 27px;
  }

  .safari-guide-title {
    font-size: clamp(24px, 7.4vw, 28px);
  }

  .safari-guide-lead {
    margin: 8px 0 13px;
    font-size: 11px;
  }

  .safari-step {
    grid-template-columns: 27px 39px minmax(0, 1fr);
    min-height: 62px;
    gap: 7px;
    padding: 7px 9px;
  }

  .safari-step__number {
    width: 24px;
    height: 24px;
    font-size: 13px;
  }

  .menu-dots-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
  }

  .safari-step__icon {
    --icon-size: 36px;
  }

  .safari-step__text strong {
    font-size: 15px;
  }

  .safari-step__text small {
    font-size: 8px;
  }

  .guide-back {
    min-height: 42px;
    margin-top: 7px;
    font-size: 15px;
  }
}
</style>
